import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { StepPageActions } from '@/global/components';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { BaseAudience } from '@/models/audiences';
import { ConnectorFlowDTO, CustomConnectorDto } from '@/models/connectors';
import { useGetConnectorInstancesQuery, usePrepareConnectorFlowQuery } from '@/services/endpoints/workspace-connectors';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { AUTO_FILL_AUDIENCE_PARAMS_FORM_IDS } from '@/features/destinations/components/connectorFormConfig';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiStepFlowForm } from './MultiStepFlowForm';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCompanyValues } from '@/providers';

type CustomConnectorFlowStepProps = {
  audience: BaseAudience;
  connector: CustomConnectorDto;
  flow: ConnectorFlowDTO;
  onNext: (params: Record<string, any>, mappings: Record<string, any>) => void;
  onCancel: () => void;
};

export const CustomConnectorFlowStep: FC<CustomConnectorFlowStepProps> = ({ audience, connector, flow, onNext, onCancel }) => {
  const { t } = useTranslation();
  const { id: workspaceId } = useWorkspace();
  const { data: instances } = useGetConnectorInstancesQuery({ workspaceId });

  const [formData, setFormData] = useState({});
  const [mappingData, setMappingData] = useState({});
  const [multiStepMappingsConfig, setMultiStepMappingsConfig] = useState<{
    schema: any;
    uiSchema: Record<string, any>;
  } | null>(null);
  const { currentLanguage } = useCompanyValues();

  // Get the active configuration based on currentVersion
  const activeFlowConfig = useMemo(() => {
    const currentVersion = flow.configuration?.currentVersion;
    const configurations = flow.configuration?.configurations || [];

    if (currentVersion && configurations.length > 0) {
      const config = configurations.find((c: { version: string }) => c.version === currentVersion);
      return config?.elements || configurations[0]?.elements;
    }
    return configurations[0]?.elements || flow.configuration?.elements;
  }, [flow.configuration]);

  // Detect if the flow is multi-step (has step1, step2, etc. in params)
  const isMultiStep = useMemo(() => {
    if (!activeFlowConfig?.params) return false;
    return Object.keys(activeFlowConfig.params).some((key) => /^step\d+$/.test(key));
  }, [activeFlowConfig]);

  // Get connector instance for this connector
  const connectorInstance = useMemo(() => {
    return instances?.find((inst) => inst.connectorId === connector.id);
  }, [instances, connector.id]);

  // For single-step flows, use the existing GET /prepare endpoint
  const { data: preparedFlow, isLoading: isPreparingFlow } = usePrepareConnectorFlowQuery(
    {
      workspaceId,
      id: flow.id,
      audienceId: audience.id,
    },
    { skip: isMultiStep } // Skip if multi-step - we'll use POST /prepare/{stepId} instead
  );

  const translatedPreparedFlow = useMemo(() => {
    if (!preparedFlow) return null;
    return translateI18nValues(preparedFlow, currentLanguage);
  }, [preparedFlow]);

  // For single-step flows, get the configuration from the prepared response
  const singleStepConfig = useMemo(() => {
    if (isMultiStep) return null;

    const currentVersion = translatedPreparedFlow?.configuration?.currentVersion;
    const configurations = translatedPreparedFlow?.configuration?.configurations || [];

    let config;
    if (currentVersion && configurations.length > 0) {
      config = configurations.find((c: { version: string }) => c.version === currentVersion)?.elements;
    }
    return config || configurations[0]?.elements || translatedPreparedFlow?.configuration?.elements;
  }, [translatedPreparedFlow, isMultiStep]);

  const paramsConfig = singleStepConfig?.params?.reactJsonSchemaForm;
  // Prefer prepared response (singleStepConfig) over static config (activeFlowConfig) - prepared has populated enums
  const mappingsConfig = singleStepConfig?.mappings?.reactJsonSchemaForm || activeFlowConfig?.mappings?.reactJsonSchemaForm;

  const isAutoFillForm = AUTO_FILL_AUDIENCE_PARAMS_FORM_IDS.includes(paramsConfig?.formId);

  useEffect(() => {
    if (!isAutoFillForm) return;
    const autoData = { audience_name: audience.name, createdOn: audience.createdOn };
    const hasMappings = mappingsConfig?.schema?.properties && Object.keys(mappingsConfig.schema.properties).length > 0;
    if (!hasMappings) {
      onNext(autoData, {});
    } else {
      setFormData(autoData);
    }
  }, [isAutoFillForm]);

  const handleSingleStepSubmit = () => {
    onNext(formData, mappingData);
  };

  const handleMultiStepComplete = (allParams: Record<string, any>, mappingsFromLastStep: { schema: any; uiSchema: Record<string, any> } | null) => {
    // Multi-step form completed, now show mappings if needed
    setFormData(allParams);

    // Store mappings config from last step (if available)
    if (mappingsFromLastStep) {
      setMultiStepMappingsConfig(mappingsFromLastStep);
    }

    // Check if we have mappings to show (prefer last step mappings, fallback to static config)
    const effectiveMappingsConfig = mappingsFromLastStep || mappingsConfig;
    if (!effectiveMappingsConfig?.schema?.properties || Object.keys(effectiveMappingsConfig.schema.properties).length === 0) {
      // No mappings needed, proceed directly
      onNext(allParams, {});
    }
    // If mappings are needed, the form will show them below
  };

  const handleFinalSubmit = () => {
    onNext(formData, mappingData);
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON:cancel',
    onClick: onCancel,
  };

  const nextButton: StepPageActionsButton = {
    text: 'COMMON:confirm',
    onClick: isMultiStep && Object.keys(formData).length > 0 ? handleFinalSubmit : handleSingleStepSubmit,
    disabled: !formData || Object.keys(formData).length === 0,
  };

  // Show loading while fetching connector instances for multi-step
  if (isMultiStep && !connectorInstance) {
    return (
      <div className='flex grow flex-col gap-6 p-6'>
        <Loadable isLoading={true}>
          <div />
        </Loadable>
      </div>
    );
  }

  return (
    <div className='flex grow flex-col gap-6 p-6'>
      <div className='flex flex-col border-b border-secondary-border pb-8'>
        <h2 className='text-lg font-semibold leading-7 text-primary-900'>{t('ALL:destinations.steps.selectProperties.title')}</h2>
        <h4 className='text-sm leading-5 text-tertiary-600'>
          {t('ALL:destinations.steps.configureFlow.description', { audienceName: audience.name, connectorName: connector.name })}
        </h4>
      </div>

      {isMultiStep ? (
        // Multi-step flow
        <div className='space-y-8'>
          {Object.keys(formData)?.length === 0 ? (
            // Show multi-step form for params
            <MultiStepFlowForm
              audience={audience}
              connectorInstance={connectorInstance!}
              flow={flow}
              paramsConfig={activeFlowConfig.params}
              onComplete={handleMultiStepComplete}
            />
          ) : (
            // Multi-step params completed, show mappings if available
            <>
              {/* <div className='rounded-lg bg-green-50 p-4'>
                <h3 className='font-medium text-green-900'>{t('ALL:destinations.steps.configureFlow.parametersConfigured')}</h3>
                <p className='text-sm text-green-700'>
                  {Object.keys(formData).length} {t('ALL:destinations.steps.configureFlow.parametersSet')}
                </p>
              </div> */}

              {(() => {
                // Use mappings from last step if available, otherwise fall back to static config
                const effectiveMappings = multiStepMappingsConfig || mappingsConfig;
                if (!effectiveMappings?.schema?.properties || Object.keys(effectiveMappings.schema.properties).length === 0) {
                  return null;
                }
                return (
                  <div>
                    <h3 className='mb-4 text-lg font-medium'>{t('ALL:destinations.steps.configureFlow.dataMapping')}</h3>
                    <Form
                      schema={effectiveMappings.schema}
                      uiSchema={{
                        ...effectiveMappings.uiSchema,
                        userDataAddress: {
                          'ui:classNames': 'hide-control-label-checkbox',
                          ...effectiveMappings.uiSchema?.userDataAddress,
                        },
                        userDataIdentifier: {
                          'ui:classNames': 'hide-control-label-checkbox',
                          ...effectiveMappings.uiSchema?.userDataIdentifier,
                        },
                      }}
                      formData={mappingData}
                      onChange={({ formData }) => setMappingData(formData)}
                      validator={validator}
                    >
                      <Fragment />
                    </Form>
                  </div>
                );
              })()}

              <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
            </>
          )}
        </div>
      ) : (
        // Single-step flow (existing logic)
        <Loadable isLoading={isPreparingFlow}>
          <div className='space-y-8'>
            {paramsConfig?.schema && paramsConfig?.uiSchema && !isAutoFillForm && (
              <div>
                <h3 className='mb-4 text-lg font-medium'>{t('ALL:destinations.steps.configureFlow.flowParameters')}</h3>
                <Form
                  schema={paramsConfig.schema}
                  uiSchema={paramsConfig.uiSchema}
                  formData={formData}
                  onChange={({ formData }) => setFormData(formData)}
                  validator={validator}
                >
                  <Fragment />
                </Form>
              </div>
            )}

            {mappingsConfig?.schema?.properties && Object.keys(mappingsConfig.schema.properties).length > 0 && (
              <div>
                <h3 className='mb-4 text-lg font-medium'>{t('ALL:destinations.steps.configureFlow.dataMapping')}</h3>
                <Form
                  schema={mappingsConfig.schema}
                  uiSchema={{
                    ...mappingsConfig.uiSchema,
                    userDataAddress: {
                      'ui:classNames': 'hide-control-label-checkbox',
                      ...mappingsConfig.uiSchema?.userDataAddress,
                    },
                    userDataIdentifier: {
                      'ui:classNames': 'hide-control-label-checkbox',
                      ...mappingsConfig.uiSchema?.userDataIdentifier,
                    },
                  }}
                  formData={mappingData}
                  onChange={({ formData }) => setMappingData(formData)}
                  validator={validator}
                >
                  <Fragment />
                </Form>
              </div>
            )}
          </div>
        </Loadable>
      )}

      {/* Only show actions for single-step or when multi-step hasn't started params yet */}
      {!isMultiStep && <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />}
    </div>
  );
};
